// plugins/error-handler.ts
import * as Sentry from '@sentry/vue';

export default defineNuxtPlugin((nuxtApp) => {
  // Global Vue error handler
  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    // Get component information if available
    const componentName = instance?.$.type?.name || 'Unknown Component';

    // Check if we're in browser environment
    if (process.client) {
      const errorInfo = {
        componentName,
        errorInfo: info,
        route: window.location.pathname,
        timestamp: new Date().toISOString()
      };

      // Capture in Sentry with context
      Sentry.captureException(error, {
        level: 'error',
        tags: {
          componentName,
          type: 'vue-error',
          route: window.location.pathname
        },
        extra: errorInfo
      });
    }

    console.error('Vue Error:', error);
  };

  // Only add browser-specific error handlers in client mode
  if (process.client) {
    // Global Promise error handler
    window.addEventListener('unhandledrejection', (event) => {
      Sentry.captureException(event.reason, {
        level: 'error',
        tags: {
          type: 'promise-rejection',
          route: window.location.pathname
        }
      });
    });

    // Global error handler
    window.onerror = function (message, source, lineno, colno, error) {
      Sentry.captureException(error || message, {
        level: 'error',
        tags: {
          type: 'window-error',
          route: window.location.pathname
        },
        extra: {
          source,
          lineno,
          colno
        }
      });
    };
  }

  // Custom error function that can be used anywhere in the app
  const captureError = (error: any, context: any = {}) => {
    const errorObject =
      error instanceof Error ? error : new Error(String(error));

    // Check for client environment before using window
    if (process.client) {
      Sentry.captureException(errorObject, {
        level: 'error',
        tags: {
          ...context.tags,
          route: window.location.pathname
        },
        extra: {
          ...context.extra,
          timestamp: new Date().toISOString()
        }
      });
    } else {
      // Server-side error capture (without window references)
      Sentry.captureException(errorObject, {
        level: 'error',
        tags: {
          ...context.tags,
          environment: 'server'
        },
        extra: {
          ...context.extra,
          timestamp: new Date().toISOString()
        }
      });
    }
  };

  // Make it available throughout the app
  return {
    provide: {
      captureError
    }
  };
});
